import { useState, useEffect, useCallback } from 'react';
import useSocket from './useSocket';

const useVehicleTracking = (initialVehicles = [], initialGeofences = []) => {
  const [vehicles, setVehicles] = useState(initialVehicles);
  const [alerts, setAlerts] = useState([]);
  const { isConnected, subscribe } = useSocket();
  
  // Update a vehicle's position
  const updateVehiclePosition = useCallback((vehicleId, position) => {
    setVehicles(prevVehicles => {
      return prevVehicles.map(vehicle => {
        if (vehicle.id === vehicleId) {
          return {
            ...vehicle,
            currentPosition: position,
            lastUpdate: new Date().toISOString()
          };
        }
        return vehicle;
      });
    });
  }, []);
  
  // Add a new alert
  const addAlert = useCallback((alert) => {
    setAlerts(prevAlerts => [alert, ...prevAlerts]);
  }, []);
  
  // Function to simulate vehicle movement (exposed for demo purposes)
  const simulateVehicleMovement = useCallback(() => {
    setVehicles(prev => prev.map(vehicle => {
      // Random small movement
      const latChange = (Math.random() - 0.5) * 0.001;
      const lngChange = (Math.random() - 0.5) * 0.001;
      
      return {
        ...vehicle,
        currentPosition: {
          lat: vehicle.currentPosition.lat + latChange,
          lng: vehicle.currentPosition.lng + lngChange,
          timestamp: new Date().toISOString()
        },
        lastUpdate: new Date().toISOString()
      };
    }));
  }, []);
  
  // Subscribe to vehicle position updates and alerts
  useEffect(() => {
    if (!isConnected) return;
    
    // Subscribe to vehicle position updates
    const positionUnsubscribe = subscribe('vehicle-position', (data) => {
      updateVehiclePosition(data.vehicleId, data.position);
    });
    
    // Subscribe to geofence alerts
    const alertUnsubscribe = subscribe('geofence-alert', (data) => {
      addAlert({
        id: `a-${Date.now()}`,
        type: `geofence_${data.type}`,
        vehicle: { id: data.vehicleId },
        geofence: { id: data.geofenceId },
        message: data.message,
        severity: 'info',
        acknowledged: false,
        createdAt: new Date().toISOString()
      });
    });
    
    // Cleanup
    return () => {
      positionUnsubscribe();
      alertUnsubscribe();
    };
  }, [isConnected, subscribe, updateVehiclePosition, addAlert]);
  
  // Initialize with vehicles passed in, but only on first render or if ID list changes
  useEffect(() => {
    if (initialVehicles.length > 0) {
      // Only update if the vehicle IDs have changed to prevent unnecessary rerenders
      setVehicles(prevVehicles => {
        // Get sets of IDs for comparison
        const prevIds = new Set(prevVehicles.map(v => v.id));
        const newIds = new Set(initialVehicles.map(v => v.id));
        
        // Check if the IDs are the same
        const sameIds = prevIds.size === newIds.size && 
          [...prevIds].every(id => newIds.has(id));
          
        // Only update if the IDs have changed
        return sameIds ? prevVehicles : initialVehicles;
      });
    }
  }, [initialVehicles]);
  
  // Check if a vehicle is inside a geofence
  const isVehicleInGeofence = useCallback((vehicle, geofence) => {
    if (!vehicle.currentPosition || !geofence) return false;
    
    if (geofence.type === 'circle' && geofence.center && geofence.radius) {
      // Calculate distance in meters between two points
      const R = 6371e3; // Earth radius in meters
      const lat1 = vehicle.currentPosition.lat * Math.PI / 180;
      const lat2 = geofence.center.lat * Math.PI / 180;
      const deltaLat = (geofence.center.lat - vehicle.currentPosition.lat) * Math.PI / 180;
      const deltaLng = (geofence.center.lng - vehicle.currentPosition.lng) * Math.PI / 180;
      
      const a = Math.sin(deltaLat/2) * Math.sin(deltaLat/2) +
                Math.cos(lat1) * Math.cos(lat2) *
                Math.sin(deltaLng/2) * Math.sin(deltaLng/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      const distance = R * c;
      
      return distance <= geofence.radius;
    }
    
    return false;
  }, []);
  
  // Track geofence status for each vehicle
  const [vehicleGeofenceState, setVehicleGeofenceState] = useState({});
  
  // Add automatic movement simulation for demo purposes
  useEffect(() => {
    if (isConnected) {
      const movementInterval = setInterval(() => {
        simulateVehicleMovement();
      }, 3000); // Update every 3 seconds
      
      return () => clearInterval(movementInterval);
    }
  }, [isConnected, simulateVehicleMovement]);
  
  // Check for geofence entries/exits
  const checkGeofenceEvents = useCallback((vehicles, geofences) => {
    if (!vehicles || !geofences || !vehicles.length || !geofences.length) return;
    
    vehicles.forEach(vehicle => {
      // Initialize state for new vehicles
      if (!vehicleGeofenceState[vehicle.id]) {
        const initialState = {};
        geofences.forEach(geofence => {
          initialState[geofence.id] = isVehicleInGeofence(vehicle, geofence);
        });
        setVehicleGeofenceState(prev => ({
          ...prev,
          [vehicle.id]: initialState
        }));
        return;
      }
      
      // Check for changes
      geofences.forEach(geofence => {
        const wasInside = vehicleGeofenceState[vehicle.id]?.[geofence.id];
        const isInside = isVehicleInGeofence(vehicle, geofence);
        
        // If state changed, trigger an alert
        if (wasInside !== undefined && wasInside !== isInside) {
          const eventType = isInside ? 'entry' : 'exit';
          addAlert({
            id: `a-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            type: `geofence_${eventType}`,
            vehicleId: vehicle.id,
            geofenceId: geofence.id,
            vehicle: { id: vehicle.id, name: vehicle.name },
            geofence: { id: geofence.id, name: geofence.name },
            createdAt: new Date().toISOString(),
            message: `${vehicle.name} ${isInside ? 'entered' : 'exited'} ${geofence.name}`,
            severity: eventType === 'exit' ? 'warning' : 'info',
            acknowledged: false
          });
          
          // Update state
          setVehicleGeofenceState(prev => ({
            ...prev,
            [vehicle.id]: {
              ...prev[vehicle.id],
              [geofence.id]: isInside
            }
          }));
        }
      });
    });
  }, [vehicleGeofenceState, isVehicleInGeofence, addAlert]);
  
  // Function to check geofence events when vehicles or geofences change
  useEffect(() => {
    if (isConnected) {
      checkGeofenceEvents(vehicles, initialGeofences);
    }
  }, [vehicles, isConnected, checkGeofenceEvents, initialGeofences]);
  
  return {
    vehicles,
    setVehicles,
    alerts,
    setAlerts,
    isConnected,
    updateVehiclePosition,
    simulateVehicleMovement,
    isVehicleInGeofence,
    checkGeofenceEvents
  };
};

export default useVehicleTracking;